import * as _ from 'lodash';
import { DataShape } from 'redux-form';

import { diagnostics, inputFormatters, utils } from '@blend/ui-common';

import * as inputUtils from 'login-components/Common/Inputs/inputUtils';
import { addressInputDefinitions } from 'login-components/Common/DataInputs/AddressInput/AddressInput';

import AddressInput from 'login-components/Common/DataInputs/AddressInput/AddressInput';
import Captcha from 'login-components/Common/Inputs/Captcha/Captcha';
import CheckboxInput from 'login-components/Common/Inputs/CheckboxInput/CheckboxInput';
import RequirementInput from 'login-components/Common/Inputs/RequirementInput/RequirementInput';
import UnformattedRequirementInput from 'login-components/Common/Inputs/RequirementInput/UnformattedRequirementInput';
import EmailInput from 'login-components/Common/Inputs/EmailInput/EmailInput';
import { DocumentWrapper, getWindow } from './globalWrappers';
import assert from './assert';

const { dateUtils } = utils;
const { MIN_DATE, MAX_DATE } = inputFormatters.DATE;

type ValidationErrorType = 'valueLengthInvalid' | 'valueRequired' | 'valueMatchInvalid' | 'valueInvalid';
interface ValidationError {
  text: string;
  type: ValidationErrorType;
}

type IValidationErrorValue = ValidationError | null;

interface ErrorMapWithTypes {
  [inputName: string]: ErrorMapWithTypes | IValidationErrorValue;
}

type IValidatorFunction = (value: string) => IValidationErrorValue;

type IValidatorFunctionWithInput = (value: string, input: FormInput) => IValidationErrorValue;

/**
 * Custom validator for cashDownPayment field since it depends on the purchasePrice field. Should be used as the
 * `validate` property in `IFormInput` inputs.
 */
export function cashDownPaymentValidator(value, allValues) {
  const validatePositiveInteger = generateValidatePositiveInteger('Down payment must be greater than or equal to 0.');
  const validationError = validatePositiveInteger(value);
  const purchasePrice = parseInt(allValues.purchasePrice, 10);
  const cashDownPayment = parseInt(value, 10);

  if (validationError) {
    return validationError;
  } else if (cashDownPayment > purchasePrice) {
    return 'Please enter a down payment that is less than the purchase price.';
  }
}

/**
 * Custom validator for loanAmount field since it depends on the propertyValue field. Should be used as the
 * `validate` property in `IFormInput` inputs.
 */
export function loanAmountValidator(value, allValues) {
  const validatePositiveInteger = generateValidatePositiveInteger('Loan amount must be greater than or equal to 0.');
  const validationError = validatePositiveInteger(value);
  const propertyValue = parseInt(allValues.propertyValue, 10);
  const loanAmount = parseInt(value, 10);

  if (validationError) {
    return validationError;
  } else if (loanAmount > propertyValue) {
    return 'Please enter a loan amount that is less than the property value.';
  }
}

/**
 * Field Level Validations
 */
export function generateValidatePositiveInteger(message?: string) {
  return function(value) {
    const int = parseInt(value, 10);
    if (int < 0) {
      return message || 'Please enter a positive value.';
    }
  };
}

export function validateDate(value: string) {
  if (!value || _.includes(value, '_')) {
    return 'Please enter a complete date.';
  }

  const { locale } = getWindow().DEPLOYMENT_CONFIG;
  const dateObj = dateUtils.parseDate(locale)(value);
  const monthIndex = parseInt(dateObj.month, 10) - 1; // becuase month is 0 indexed
  const dateValue = new Date(parseInt(dateObj.year, 10), monthIndex, parseInt(dateObj.day, 10));

  const milliseconds = dateValue.getTime();

  // if we got this far, we know we have a full date and can reliably parse off the year
  // we do so because if you pass a string like 11/11/0011, new Date() interprets it as a two-digit year (i.e. 2011)
  // & we want to validate the 4-digit year as typed. For numbers, we can safely grab the year using Date.getFullYear.
  const year = parseInt(value.substr('mm/dd/'.length, 4), 10);

  if (year < MIN_DATE.year || milliseconds > MAX_DATE.valueOf() || milliseconds < MIN_DATE.valueOf()) {
    const [minString, maxString] = _.map([MIN_DATE, MAX_DATE], dateTime => dateTime.toFormat('MM/dd/yyyy'));
    return `Please enter a date between ${minString} and ${maxString}.`;
  }

  if (dateValue && isNaN(milliseconds)) {
    return 'Please enter a valid date.';
  }

  // dateValue will modify the day value if it is invalid, like 02/30/2018 will become 03/02/2018. Therefore, if the
  // date of dateValue is not the same as input date value, than we conclude it is a invalid date
  const day = parseInt(dateObj.day, 10);

  if (dateValue.getDate() !== day) {
    return 'That date doesn\'t exist';
  }

  return undefined;
}

export function generateValidateNoDisallowedCharacters(invalidChars: string[], message?: string) {
  return function(value) {
    const invalidCharsInValue = _.filter(invalidChars, char => _.includes(value, char));
    return invalidCharsInValue.length > 0
      ? message ||
          `This field cannot contain the following character${invalidCharsInValue.length === 1 ? '' : 's'}: ${_.join(
            invalidCharsInValue,
            '',
          )}`
      : undefined;
  };
}

/**
 * Legacy Validation Functions
 * These functions are used in the form-level validate and do some field-level validations
 */
const validateEmail: IValidatorFunction = (value: string) => {
  const emailRegEx = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
  return emailRegEx.test(value)
    ? null
    : {
        text: 'Please enter a valid email.',
        type: 'valueInvalid',
      };
};

const getTruthinessErrorForInput = (input: FormInput) => {
  switch (input.component) {
    case Captcha:
      return `Please accept the captcha.`;
    case RequirementInput:
    case UnformattedRequirementInput:
      return `Confirm that you've read and agree to the terms above.`;
    case CheckboxInput:
      return `Please confirm that you ${input.identifier}.`;
    default:
      return `Please enter your ${input.identifier}.`;
  }
};

const validateTruthiness: IValidatorFunctionWithInput = (value: string, input: FormInput) => {
  if (input.optional) {
    return null;
  }
  return value
    ? null
    : {
        text: getTruthinessErrorForInput(input),
        type: 'valueRequired',
      };
};

const getLengthErrorForInput = (input: FormInput, requiredLength: number) => {
  switch (input.component) {
    default:
      return `Your ${input.identifier} must be ${requiredLength} characters long.`;
  }
};

const validateLength: IValidatorFunctionWithInput = (value: string, input: FormInput) => {
  const { requiredLength } = input;
  if (_.isUndefined(requiredLength)) {
    return null;
  }

  const isValid = (() => {
    // If an input is optional and empty, let it through, but if it's optional and
    // NOT empty, validate the length to not permit invalid data through.
    if (input.optional && !value) {
      return true;
    }
    return value && value.length === requiredLength;
  })();

  return isValid
    ? null
    : {
        text: getLengthErrorForInput(input, requiredLength),
        type: 'valueLengthInvalid',
      };
};

type SyncErrorValue = SyncErrors | string;

interface SyncErrors {
  [propName: string]: SyncErrorValue;
}

const isValueRequirementError = (error: IValidationErrorValue) => {
  return error && error.type === 'valueRequired';
};

const isValidationError = error => {
  const errorTypes: ValidationErrorType[] = [
    'valueLengthInvalid',
    'valueRequired',
    'valueMatchInvalid',
    'valueInvalid',
  ];
  return error && _.includes(errorTypes, error.type);
};

const getErrorText = (errors: ErrorMapWithTypes) => {
  const normalizedErrors: SyncErrors = _.transform<any, any>(
    errors,
    (acc: any, errorValue: any, errorKey: string) => {
      // Base case -- we've already resolved the real error value.
      if (_.isString(errorValue) || _.isNil(errorValue)) {
        acc[errorKey] = errorValue;
      } else {
        // We either have a value that's a validation error or is a nested property, like an address.
        acc[errorKey] = isValidationError(errorValue) ? errorValue.text : getErrorText(errorValue);
      }
    },
    {},
  );

  return normalizedErrors;
};

type IContextValidator = (inputs: FormInput[], values: DataShape) => ErrorMapWithTypes;

export const validateWithContext: IContextValidator = (inputs, values) => {
  const validationObj = inputs.reduce(function(errors, input) {
    const thisVal = values[input.name];

    if (input.component === EmailInput) {
      errors[input.name] = validateEmail(thisVal);
      return errors;
    }

    if (input.component === AddressInput) {
      // If the parent AddressInput is optional, we expect *all* of the child fields to be either
      // empty or valid, even fields that are normally optional.
      if (input.optional) {
        const allInputsRequiredState = _.map(_.cloneDeep(addressInputDefinitions), inputDefinition => {
          delete inputDefinition.optional;
          return inputDefinition;
        });

        const allInputErrors = validateWithContext(
          allInputsRequiredState,
          thisVal || /* istanbul ignore next: (UI-5482) */ {},
        );
        const allFieldsEmpty = _.every(addressInputDefinitions, ({ name }) => {
          const validationError = allInputErrors[name] as IValidationErrorValue;
          return isValueRequirementError(validationError);
        });

        if (allFieldsEmpty) {
          return errors;
        }
      }

      // If all fields *aren't* empty, we fall through to normal handling.
      const addressErrors = validateWithContext(addressInputDefinitions, thisVal || {});
      if (!_.isEmpty(addressErrors)) {
        errors[input.name] = addressErrors;
        return errors;
      }
    }

    // This currently assumes that all inputs are required.
    const truthinessError = validateTruthiness(thisVal, input);
    if (truthinessError) {
      errors[input.name] = truthinessError;
      return errors;
    }

    if (input.validateMatch) {
      assert(
        !!_.find(inputs, { name: input.validateMatch }),
        `No paired input for ${input.name} to validate against. Expected input with name ${input.validateMatch}.`,
      );

      const matchValue = values[input.validateMatch];
      if (!_.isEqual(matchValue, thisVal)) {
        if (
          !!matchValue &&
          !!thisVal &&
          !!input.validateMatchCaseInsensitive &&
          _.isEqual(matchValue.toLowerCase(), thisVal.toLowerCase())
        ) {
          return errors;
        }
        errors[input.name] = {
          text: `This must match ${input.validateMatch}.`,
          type: 'valueMatchInvalid',
        };
        return errors;
      }
    }

    const lengthValidationError = validateLength(thisVal, input);
    if (lengthValidationError) {
      errors[input.name] = lengthValidationError;
      return errors;
    }

    return errors;
  }, {});

  return validationObj;
};

type ISyncValidator = (inputs: FormInput[], values: DataShape) => SyncErrors;

export const validate: ISyncValidator = (inputs, values) => {
  const errorsWithContext = validateWithContext(inputs, values);
  const normalizedErrors = getErrorText(errorsWithContext);
  return normalizedErrors;
};

interface ErrorIdentifier {
  fieldId: string;
  error: SyncErrorValue;
}

const getIdToFocus = (errors: ErrorIdentifier[], globalErrorId?: string) => {
  const inputNameToFocus = _.first(errors)!.fieldId;
  const idToFocus = inputUtils.getIdFromName(inputNameToFocus);

  // _error is the form-level error property for redux form
  if (idToFocus === '_error') {
    if (!globalErrorId) {
      diagnostics.error(new Error(`No global errorId defined on form.`));
      return null;
    }

    return globalErrorId;
  }

  return idToFocus;
};

type IFlattenNestedErrors = (parentKey: string, errorValue: SyncErrors) => ErrorIdentifier[];

const flattenNestedErrors: IFlattenNestedErrors = (parentKey, errorValue) => {
  // There's a recursive relationship between this and getValidErrors
  const errorValues = getValidErrors(errorValue);
  const errorsWithParentKey: ErrorIdentifier[] = _.map(errorValues, ({ fieldId, error }) => {
    return {
      fieldId: `${parentKey}.${fieldId}`,
      error,
    };
  });

  return errorsWithParentKey;
};

type IGetValidErrors = (errors: SyncErrors) => ErrorIdentifier[];

const getValidErrors: IGetValidErrors = (syncErrors: SyncErrors) => {
  const trueErrors = _.transform<any, ErrorIdentifier>(
    syncErrors,
    (acc, errorValue: SyncErrorValue, key: string) => {
      if (!errorValue) {
        return null;
      }

      // Errors for nested inputs (e.g. the address input) are objects, so we need to handle that.
      if (_.isObject(errorValue)) {
        const flattenedErrors = flattenNestedErrors(key, errorValue as SyncErrors);
        return acc.push(...flattenedErrors);
      }

      acc.push({ fieldId: key, error: errorValue });
    },
    [],
  );

  return trueErrors;
};

export const focusOnSubmitFail = (syncErrors, globalErrorId?: string) => {
  const trueErrors = getValidErrors(syncErrors);
  if (_.isEmpty(trueErrors)) {
    return;
  }

  const idToFocus = getIdToFocus(trueErrors, globalErrorId);
  if (!idToFocus) {
    return;
  }

  const elementToFocus = DocumentWrapper.getElementById(idToFocus);
  if (!elementToFocus) {
    diagnostics.error(new Error(`No element found for id ${idToFocus}.`));
    return;
  }

  elementToFocus.focus();
};

export const submitForm = (form: HTMLFormElement) => {
  return form.submit();
};

export const getEmailDisclaimerText = (): string | undefined => {
  const window = getWindow();
  return _.get(window.DEPLOYMENT_CONFIG, 'organization.emailDisclaimerText');
};
